import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import { Title, Loader, ClearFilters } from '../../elements';
import { VeterinaryLocationSupplyFeedModal, VeterinaryLocationVaccinationModal } from '../../components';
import { AnimalType } from '../../models/animal';
import {
  Box,
  Button,
  Chip,
  Container,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_BUTTON_DEFAULT, V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { VetService } from '../../hooks/VetService';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faSyringe } from '@fortawesome/free-solid-svg-icons';
import SearchIcon from '@mui/icons-material/Search';
import ListIcon from '@mui/icons-material/List';
import { AnimalTypeSelect, LocationPositionSelect, LocationStableSelect } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { LocationViewModel, LocationPosition, LocationStable } from '../../models/locations';
import { VetsMedicalSupplyViewModel } from '../../models/vets';
import { VeterinaryLocationSupplyFeedModalResult } from '../../components/VeterinaryLocationSupplyFeedModal/component';
import { VeterinaryLocationVaccinationModalResult } from '../../components/VeterinaryLocationVaccinationModal/component';
import { MedicalSupplyMethod } from '../../models/injection';

const VeterinaryLocations: React.FC<any> = () => {
  const styles = sxStyles(useTheme());

  const vetService = new VetService();

  const { t } = useTranslation();

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [locations, setLocations] = React.useState([] as LocationViewModel[]);
  const [location, setLocation] = React.useState({} as LocationViewModel);

  const [farmSupplies, setFarmSupplies] = React.useState([] as VetsMedicalSupplyViewModel[]);
  const [vaccinationSupplies, setVaccinationSupplies] = React.useState([] as VetsMedicalSupplyViewModel[]);

  const [animalType, setAnimalType] = React.useState(0);
  const [locationPosition, setLocationPosition] = React.useState(0);
  const [locationStable, setLocationStable] = React.useState(0);

  const loadFarmLocations = async () => {
    setIsBusy(true);
    setTotal(0);

    const searchPage = page === 0 ? 1 : page + 1;

    vetService
      .getVetLocationsByQuery(searchPage, rowsPerPage, number, room, pen, locationPosition, locationStable, animalType)
      .then((result) => {
        setTotal(result.total);
        setLocations(result.data);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  const loadFarmMedicalSupplies = async (location: LocationViewModel) => {
    setIsBusy(true);

    vetService
      .getVetMedicalSupplies(location.farmId)
      .then((supplies) => {
        setFarmSupplies(supplies.filter((supply) => supply.method === MedicalSupplyMethod.Oral));
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  // Vaccination supplies are injectable, so we do not apply the Oral method
  // filter that the oral feed flow uses.
  const loadVaccinationSupplies = async (location: LocationViewModel) => {
    setIsBusy(true);

    vetService
      .getVetMedicalSupplies(location.farmId)
      .then((supplies) => {
        setVaccinationSupplies(supplies.filter((supply) => supply.method !== MedicalSupplyMethod.Oral));
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadFarmLocations();
      loadData();
    }
  }, [loaded]);

  const querySearch = () => {
    setPage(0);
    setLoaded(false);
  };

  const clearFilters = () => {
    setNumber('');
    setRoom('');
    setPen('');
    setAnimalType(0);
    setLocationPosition(0);
    setLocationStable(0);
  };

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setLoaded(false);
  };
  // * pagination

  // location number text control
  const [number, setNumber] = React.useState('');

  const handleLocationNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumber(event.target.value);
  };
  // * location number text control
  // location room text control
  const [room, setRoom] = React.useState('');

  const handleLocationRoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoom(event.target.value);
  };
  // * location room text control
  // location pen text control
  const [pen, setPen] = React.useState('');

  const handleLocationPenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPen(event.target.value);
  };
  // * location pen text control

  // medical supply oral feed modal
  const [supplyFeedDialogOpen, setSupplyFeedDialogOpen] = React.useState(false);

  const handleVetFeedMedicalSupply = (result: VeterinaryLocationSupplyFeedModalResult | null) => {
    setSupplyFeedDialogOpen(false);

    if (result === null) {
      return;
    }

    vetService
      .createVetOralFeedInjection(result.farmSupplyId, location.id, result.dose)
      .then(() => {
        notifications.success(t('f-injections-oral-ok'));
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };
  // * medical supply oral feed modal

  // gilts vaccination modal
  const [vaccinationDialogOpen, setVaccinationDialogOpen] = React.useState(false);

  const handleVetVaccinateGilts = (result: VeterinaryLocationVaccinationModalResult | null) => {
    setVaccinationDialogOpen(false);

    if (result === null) {
      return;
    }

    vetService
      .createVetVaccinationInjection(result.farmSupplyId, location.id, result.repeatDays, result.dose)
      .then((injectionsCreated) => {
        if (!injectionsCreated || injectionsCreated.length === 0) {
          notifications.warning(t('f-loc-vaccination-no-gilts'));
          return;
        }

        notifications.success(t('f-loc-vaccination-ok'));
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };
  // * gilts vaccination modal

  const renderAnimalTypeText = (animalType: AnimalType) => {
    if (animalType === AnimalType.Unknown) {
      return '-';
    }
    return AnimalType[animalType];
  };

  const renderLocationStableText = (stable: LocationStable) => {
    if (stable === LocationStable.Unknown) {
      return '-';
    }
    return LocationStable[stable];
  };

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Grid container spacing={2} id="vetLocationsComponent">
          <Grid item container direction="row" sx={styles.paperHeader}>
            <Grid item container md={6} xs={8} spacing={1}>
              <Grid item>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-brand" size="2x" />
              </Grid>
              <Grid item>
                <Title>{t('f-loc-search-title')}</Title>
              </Grid>
            </Grid>
          </Grid>

          {/* filters section */}
          <Grid item container direction="row" spacing={2}>
            <Grid item lg={3} md={4} sm={6} xs={12}>
              <TextField
                id="locationNumberField"
                label={t('f-chip-number')}
                variant="outlined"
                size="small"
                fullWidth
                value={number}
                onChange={handleLocationNumberChange}
              />
            </Grid>

            <Grid item lg={3} md={4} sm={6} xs={12}>
              <TextField
                id="locationRoomField"
                label={t('f-loc-room')}
                variant="outlined"
                size="small"
                fullWidth
                value={room}
                onChange={handleLocationRoomChange}
              />
            </Grid>

            <Grid item lg={3} md={4} sm={6} xs={12}>
              <TextField
                id="locationPenField"
                label={t('f-loc-pen')}
                variant="outlined"
                size="small"
                fullWidth
                value={pen}
                onChange={handleLocationPenChange}
              />
            </Grid>

            <Grid item lg={3} md={4} sm={6} xs={12}>
              <LocationPositionSelect
                value={locationPosition}
                fullWidth
                allowAny
                onLocationPositionChange={(value) => setLocationPosition(value)}
              />
            </Grid>

            <Grid item lg={3} md={4} sm={6} xs={12}>
              <LocationStableSelect
                value={locationStable}
                fullWidth
                allowAny
                onLocationStableChange={(value) => setLocationStable(value)}
              />
            </Grid>

            <Grid item lg={3} md={4} sm={6} xs={12}>
              <AnimalTypeSelect
                value={animalType}
                fullWidth
                allowAny
                onAnimalTypeChange={(type) => setAnimalType(type)}
              />
            </Grid>

            <Grid item lg={3} md={4} sm={6} xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ClearFilters onClick={clearFilters} />

                <Button
                  id="searchBtn"
                  variant="contained"
                  color="inherit"
                  fullWidth
                  onClick={querySearch}
                  startIcon={<SearchIcon />}
                  sx={V4_BUTTON_DEFAULT}
                >
                  {t('f-search')}
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Grid item container direction="row" spacing={1} sx={styles.filtersRow}></Grid>
          {/* filters section */}

          <Grid item container sx={styles.mt1} alignItems="center">
            <Grid item container spacing={1} lg={6} md={6} xs={12}>
              <ListIcon color="primary" fontSize="large" />
              <Box sx={{ ml: 1 }}>
                <Title>{t('f-search-results')}</Title>
              </Box>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {!isBusy && (
            <Grid item xs={12} sx={styles.mt1}>
              <TableContainer component={Paper} elevation={1}>
                <Table size="small" id="locationsSearchResultTable" sx={V4_TABLE}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('f-loc-name')}</TableCell>
                      <TableCell>{t('f-loc-room')}</TableCell>
                      <TableCell>{t('f-loc-pen')}</TableCell>
                      <TableCell>{t('f-loc-pos')}</TableCell>
                      <TableCell>{t('f-loc-stable')}</TableCell>
                      <TableCell>{t('f-com-animal-type')}</TableCell>
                      <TableCell>{t('f-search-number')}</TableCell>
                      <TableCell>{t('f-loc-cyc-animal-amount')}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>

                  {loaded && locations.length > 0 && (
                    <TableBody>
                      {locations.map((location) => {
                        return (
                          <TableRow key={location.id} hover>
                            <TableCell>{location.farm?.name}</TableCell>
                            <TableCell>{location.room}</TableCell>
                            <TableCell>{location.pen}</TableCell>
                            <TableCell>{LocationPosition[location.positioning]}</TableCell>
                            <TableCell>{renderLocationStableText(location.stable)}</TableCell>
                            <TableCell>{renderAnimalTypeText(location.animalType)}</TableCell>
                            <TableCell>{location.number}</TableCell>
                            <TableCell>{location.animalsCount}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip arrow title={t('f-loc-view-medication-supply')}>
                                  <IconButton
                                    color="primary"
                                    aria-label="Feed supply"
                                    size="small"
                                    onClick={() => {
                                      setLocation(location);
                                      loadFarmMedicalSupplies(location);
                                      setSupplyFeedDialogOpen(true);
                                    }}
                                    sx={V4_ICON_BUTTON_SMALL}
                                  >
                                    <Icon>{'bubble_chart'}</Icon>
                                  </IconButton>
                                </Tooltip>

                                <Tooltip arrow title={t('f-loc-vaccination-action')}>
                                  <IconButton
                                    color="primary"
                                    aria-label="Vaccinate gilts"
                                    size="small"
                                    onClick={() => {
                                      setLocation(location);
                                      loadVaccinationSupplies(location);
                                      setVaccinationDialogOpen(true);
                                    }}
                                    sx={V4_ICON_BUTTON_SMALL}
                                  >
                                    <FontAwesomeIcon icon={faSyringe} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  )}

                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </Container>

      <VeterinaryLocationSupplyFeedModal
        opened={supplyFeedDialogOpen}
        farmSupplies={farmSupplies}
        isLoaded={loaded}
        onModalAction={handleVetFeedMedicalSupply}
      />

      <VeterinaryLocationVaccinationModal
        opened={vaccinationDialogOpen}
        farmSupplies={vaccinationSupplies}
        isLoaded={loaded}
        onModalAction={handleVetVaccinateGilts}
      />
    </>
  );
};

export default VeterinaryLocations;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    formControl: {
      margin: theme.spacing(1)
    },
    bold: {
      fontWeight: 'bold'
    },
    headerRightSection: {
      flex: 1,
      textAlign: 'right',
      flexDirection: 'row-reverse'
    },
    displayNone: {
      display: 'none'
    },
    ordersTable: {
      marginTop: theme.spacing(2)
    },
    tableHeader: {
      background: colors.defaultGrey
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    paperHeaderActions: {
      justifyContent: 'flex-end'
    },
    paperHeaderIcon: {
      color: theme.palette.primary.main
    },
    withMargin: {
      margin: theme.spacing(1)
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    filtersRow: {
      alignItems: 'center'
    },
    mt1: {
      marginTop: theme.spacing(1)
    },
    mt12: {
      marginTop: '12px'
    }
  }) as const;
